import { Component, Emit, Prop, PropSync, Watch } from 'vue-property-decorator'
import GridHeader from '@/components/GridHeader.vue'
import GridDisplayOptions from '@/components/GridDisplayOptions.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { BaseCrud } from '@/shared/models/cruds.models'
import {
    initialInvoiceModalGridState,
    InvoiceModalGridState
} from '@/modules/billing-module/components/modal/invoices/crud/components/grid/billing-modal-invoices-crud-grid.model'
import {
    BillableDeliveryNoteResponse
} from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.dto'
import {
    BillingModalInvoicesCrudGridDatasource
} from '@/modules/billing-module/components/modal/invoices/crud/components/grid/config/billing-modal-invoices-crud-grid.datasource'
import { GetContextMenuItemsParams, GridReadyEvent, MenuItemDef } from '@ag-grid-enterprise/all-modules'
import { AgGridRowModelType } from '@/shared/models/ag-grid.model'
import { store } from '@/store/store'
import { InvoiceStatus, InvoiceResponse } from '@/api/services/billing/common-invoice/billing-common-invoice.dto'
import { billingCommonInvoiceService } from '@/api/services/billing/common-invoice/billing-common-invoice.service'
import {
    billingCommonDeliveryNoteService
} from '@/api/services/billing/common-delivery-note/billing-common-delivery-note.service'
import { Nullable, StrictNullable } from '@/shared/types/app.types'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { ToastUtils } from '@/shared/util/toast-utils'
import { CommonExportType, ExportCommonParams } from '@/api/resources/common/shared/models/common.models'
import { AxiosResponse } from 'axios'
import { EntityIdentifiersRequest } from '@/shared/models/http/http.model'
import { ReportGridRequest } from '@/api/services/common.dto'
import { GridUtils } from '@/shared/util/grid-utils'
import { FileUtils } from '@/shared/util/file-utils'
import { ModalUtils } from '@/shared/util/modal-utils'

@Component({
    components: {
        GridHeader,
        GridDisplayOptions,
        VtGrid
    }
})
export default class BillingModalInvoicesCrudGrid extends BaseCrudServerSideComponent<BillableDeliveryNoteResponse> implements BaseCrud {
    @PropSync('deliveryNotes', { type: Array, default: () => [] })
    syncedDeliveryNotes!: BillableDeliveryNoteResponse[]

    @Prop({ type: Object })
    readonly invoice!: Nullable<InvoiceResponse>

    @Emit()
    deliveryNotesCountChanged(count: number): number {
        return count
    }
    @Emit()
    deliveryNotesDeleted(): void {
        // emits an event
    }

    @Watch('_rowCount')
    private _onRowCountChanged(): void {
        this.deliveryNotesCountChanged(this._rowCount)
    }

    state: InvoiceModalGridState
    constructor() {
        super()
        this.state = initialInvoiceModalGridState()
    }

    /// GRID COMPUTED ///

    private static _canRemoveDeliveryNotes(deliveryNotes: BillableDeliveryNoteResponse[], invoiceStatus: Nullable<InvoiceStatus>): boolean {
        return !!deliveryNotes.length && (invoiceStatus === InvoiceStatus.GENERATED || !invoiceStatus)
    }
    get canRemoveSelectedDeliveryNotes(): boolean {
        return BillingModalInvoicesCrudGrid._canRemoveDeliveryNotes(this.state.selectedRows, this.invoice?.status.code)
    }

    get visibleRemoveSelectedDeliveryNotes(): boolean {
        return !this.invoice
    }
    get visibleOpenInvoiceReport(): boolean {
        return !!this.invoice
    }
    get visibleActions(): boolean {
        return this.visibleRemoveSelectedDeliveryNotes ||
            this.visibleOpenInvoiceReport
    }

    private get _rowCount(): number {
        if (this.invoice) {
            return this.state.gridApi?.paginationGetRowCount() ?? 0
        }
        return this.state.rowData?.length ?? 0
    }
    get gridTitle(): string {
        return `${this._rowCount} ${this.$tc('accounting.field.deliveryNote', this._rowCount)}`
    }
    get gridSubtitle(): string {
        if (this.state.selectedRows.length) {
            const choice = this.state.selectedRows.length > 1 ? 2 : 1
            return `${this.state.selectedRows.length} ${this.$tc('global.selectedElements', choice)}`
        }
        return ''
    }
    get rowModel(): string {
        return this.state.gridOptions.rowModelType ?? ''
    }

    /// VUE HOOKS ///

    mounted(): void {
        this.state.rowData = []
        this.state.datasource = new BillingModalInvoicesCrudGridDatasource(this.invoice?.id)
        if (this.invoice) {
            this.state.gridOptions.rowModelType = AgGridRowModelType.SERVER_SIDE
        } else {
            this.state.gridOptions.rowModelType = AgGridRowModelType.CLIENT_SIDE
            this.state.rowData = this.syncedDeliveryNotes
        }
    }

    /// AG GRID HOOKS ///

    private _setRowData(rowData: BillableDeliveryNoteResponse[]): void {
        if (this.state.gridApi) {
            this.state.gridApi.setRowData(rowData)
            this.state.gridApi.selectAll()
        }
    }

    onGridReady(event: GridReadyEvent): void {
        this.state.gridApi = event.api
        this.state.columnApi = event.columnApi

        if (
            this.state.gridOptions.rowModelType === AgGridRowModelType.SERVER_SIDE &&
            this.state.datasource
        ) {
            this.state.gridApi.setServerSideDatasource(this.state.datasource)
        } else {
            this.state.gridApi.setRowData(this.state.rowData ?? [])
        }
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        const menuItems: MenuItemDef[] = []
        if (params.node) {
            const selectedNode: BillableDeliveryNoteResponse = params.node.data
            if (BillingModalInvoicesCrudGrid._canRemoveDeliveryNotes([selectedNode], this.invoice?.status.code)) {
                menuItems.push({
                    name: this.$tc('accounting.action.deleteDeliveryNote'),
                    action: () => this.removeSelectedDeliveryNotes([selectedNode.id])
                })
            }
            menuItems.push({
                name: this.$tc('global.action.openReport'),
                action: () => this.openDeliveryNoteReport(selectedNode.id)
            })
        }
        return menuItems
    }

    /// SERVICE CALLS ///

    private async _removeDeliveryNotes(deliveryNoteIds: number[]): Promise<boolean> {
        if (deliveryNoteIds.length) {
            if (this.invoice) {
                const response =
                        await PromiseUtils.toCallSecure(
                            billingCommonInvoiceService.detachDeliveryNotes(
                                store.state.labId,
                                this.invoice.id,
                                { deliveryNoteIds }))
                if (response !== null) {
                    this.reloadData()
                    return true
                }
            } else {
                this.state.rowData =
                        this.state.rowData?.filter(deliveryNote => !deliveryNoteIds.includes(deliveryNote.id)) ?? []
                this._setRowData(this.state.rowData)
                this.syncedDeliveryNotes = this.state.rowData
                return true
            }
        }
        return false
    }

    private _downloadDeliveryNoteReport(billableDeliveryNoteId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingCommonDeliveryNoteService.getReport(store.state.labId, billableDeliveryNoteId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    private _downloadInvoiceReport(invoiceId: number): Promise<StrictNullable<Blob>> {
        return PromiseUtils.toCallSecure(
            billingCommonInvoiceService.getReport(store.getters.labId, invoiceId, false),
            error => ModalUtils.showErrorModalFromErrorResponse(error, this.$tc('print.modals.errorReport')))
    }

    /// GRID OPERATIONS ///

    onClearFilter(): void {
        this.state.gridApi?.setFilterModel(null)
    }

    async openDeliveryNoteReport(deliveryNoteId: number): Promise<void> {
        const blob = await this._downloadDeliveryNoteReport(deliveryNoteId)
        if (blob !== null) {
            FileUtils.openFile({ blob })
        }
    }

    async openInvoiceReport(): Promise<void> {
        if (this.invoice) {
            const blob = await this._downloadInvoiceReport(this.invoice.id)
            if (blob !== null) {
                FileUtils.openFile({ blob })
            }
        }
    }

    async exportReport(exportType: CommonExportType): Promise<AxiosResponse<Blob> | null> {
        const columns = this.getColumnsToExport()
        if (columns && this.state.gridApi && this.state.datasource?.exporter) {
            const entityIdentifiersRequest: EntityIdentifiersRequest = {
                entityIdentifiers: this.state.selectedRows.map(selectedRow => selectedRow.id)
            }
            const request: ReportGridRequest = {
                sectionLabel: this.gridTitle,
                columns,
                entityIdentifiersRequest,
                filterModel: this.state.gridApi.getFilterModel()
            }
            const params: ExportCommonParams = {
                sort: GridUtils.getSortValue(this.state.gridApi.getSortModel()),
                exportType
            }
            return this.state.datasource.exporter(request, params)
        }
        return null
    }

    async removeSelectedDeliveryNotes(
        ids = this.state.selectedRows.map(selectedRow => selectedRow.id)
    ): Promise<void> {
        if (await this._removeDeliveryNotes(ids)) {
            ToastUtils.showToastSuccess(
                this.$tc('accounting.invoice.detachDeliveryNotes', ids.length, { elements: ids.length }))
            this.deliveryNotesDeleted() // emits an event
        }
    }
}
